/**
 * Google Analytics (GA4) composable for SPA page view and event tracking.
 *
 * The GA4 Measurement ID comes from the API tenant settings (googleAnalyticsId).
 * gtag.js is loaded dynamically after settings are fetched — not hardcoded in
 * index.html. This allows each tenant to have their own GA tracking.
 *
 * Usage:
 *   import { initAnalytics, trackPageView, trackEvent } from '@/composables/useAnalytics';
 *   // After settings load:
 *   initAnalytics(settings.googleAnalyticsId);
 *   // In router.afterEach:
 *   trackPageView(to.fullPath, to.meta.title);
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

let initialized = false;
let measurementId: string | null = null;

function gtag(...args: unknown[]) {
  window.gtag?.(...args);
}

/**
 * Dynamically load gtag.js and configure with the tenant's Measurement ID.
 * Safe to call multiple times — only initializes once.
 * Skipped on localhost/local dev domains.
 */
export function initAnalytics(gaId?: string | null) {
  if (initialized || !gaId) return;

  // Skip on local development
  const host = window.location.hostname;
  const isLocal =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".local.gunclub.social") ||
    host.endsWith(".localtest.me");
  if (isLocal) return;

  measurementId = gaId;
  initialized = true;

  // Load gtag.js script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  // send_page_view: false because we track page views manually from the Vue router
  window.gtag("config", gaId, { send_page_view: false });
}

/** Send a GA4 page_view event (call from router.afterEach). */
export function trackPageView(path: string, title?: string) {
  if (!initialized) return;
  gtag("event", "page_view", {
    page_path: path,
    page_title: title || document.title,
  });
}

/** Send a custom GA4 event. */
export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>,
) {
  if (!initialized) return;
  gtag("event", eventName, params);
}

// ── Pre-built funnel events ──────────────────────────────────────────────

export function trackSignUp(method?: string) {
  trackEvent("sign_up", { method });
}

export function trackLogin(method?: string) {
  trackEvent("login", { method });
}

export function trackPostCreated(postType: string) {
  trackEvent("post_created", { post_type: postType });
}

export function trackReaction(postUlid: string, reactionName: string) {
  trackEvent("reaction", { post_ulid: postUlid, reaction_name: reactionName });
}

export function trackFollow(targetUsername: string) {
  trackEvent("follow", { target_username: targetUsername });
}

export function trackShare(postUlid: string, method?: string) {
  trackEvent("share", { post_ulid: postUlid, method });
}

export function trackSearch(query: string) {
  trackEvent("search", { search_term: query });
}

export function trackPostView(postUlid: string, postType: string) {
  trackEvent("post_view", { post_ulid: postUlid, post_type: postType });
}
